import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  AppBar,
  Toolbar,
  TextField,
  InputAdornment,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Drawer,
  IconButton,
  Chip,
  Slider,
  Paper,
  Stack,
  Divider,
  Autocomplete,
  Avatar,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Search as SearchIcon,
  DirectionsCar as CarIcon,
  FilterList as FilterIcon,
  Close as CloseIcon,
  Tune as TuneIcon,
  CalendarMonth as CalendarIcon,
  Payments as PaymentsIcon,
  Speed as SpeedIcon,
  Category as CategoryIcon,
  ArrowForward as ArrowForwardIcon,
  LocalOffer as LocalOfferIcon,
} from '@mui/icons-material';
import { publicVehicleService, } from '../../services/publicApi';
import { trackShowcaseEvent } from '../../services/showcaseAnalytics';
import { STORE_BRANDING, storeWhatsappLink } from '../../config/storeBranding';
import { Vehicle } from '../../types';

const BRAND_RED = '#DD2F30';
const API_BASE = (process.env.REACT_APP_API_URL || 'https://api.carmanager.com.br').replace(/\/$/, '');

const getImageUrl = (url: string) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${API_BASE}${url.startsWith('/') ? url : `/${url}`}`;
};

const fontHeading = '"Outfit", sans-serif';
const fontBody = '"DM Sans", sans-serif';

/** Extrai número da quilometragem (ex: "15.000" ou "15000") */
const parseKm = (km: string): number => parseInt(String(km).replace(/\D/g, ''), 10) || 0;

type SortOption = 'relevance' | 'price_asc' | 'price_desc' | 'year_desc';

const SWIPE_THRESHOLD_PX = 45;

/** Mídia do card na listagem: no mobile, arrastar horizontalmente troca a foto quando há várias imagens */
const VitrineListingCardMedia: React.FC<{ vehicle: Vehicle }> = ({ vehicle }) => {
  const theme = useTheme();
  const swipeEnabled = useMediaQuery(theme.breakpoints.down('md'));
  const imageUrls = useMemo(
    () => (vehicle.images ?? []).map((i) => (i.url ? getImageUrl(i.url) : '')).filter(Boolean),
    [vehicle.images]
  );
  const [photoIndex, setPhotoIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const blockNextClick = useRef(false);

  useEffect(() => {
    setPhotoIndex(0);
  }, [vehicle.id]);

  useEffect(() => {
    if (photoIndex >= imageUrls.length) setPhotoIndex(0);
  }, [imageUrls.length, photoIndex]);

  const canSwipe = swipeEnabled && imageUrls.length > 1;
  const currentUrl = imageUrls[photoIndex] ?? imageUrls[0];

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!canSwipe) return;
    touchStartX.current = e.touches[0].clientX;
    blockNextClick.current = false;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!canSwipe || touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(dx) < SWIPE_THRESHOLD_PX) return;
    blockNextClick.current = true;
    if (dx < 0) {
      setPhotoIndex((i) => (i + 1) % imageUrls.length);
    } else {
      setPhotoIndex((i) => (i - 1 + imageUrls.length) % imageUrls.length);
    }
  };

  const handleMediaAreaClick = (e: React.MouseEvent) => {
    if (blockNextClick.current) {
      e.preventDefault();
      e.stopPropagation();
      blockNextClick.current = false;
    }
  };

  if (!currentUrl) {
    return (
      <Box
        sx={{
          position: 'relative',
          pt: '58%',
          bgcolor: '#eceff1',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(145deg, #eceff1 0%, #dfe3e6 100%)',
          }}
        >
          <CarIcon sx={{ fontSize: 72, color: 'rgba(0,0,0,0.12)' }} />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        pt: '58%',
        bgcolor: '#eceff1',
        overflow: 'hidden',
        touchAction: canSwipe ? 'pan-y' : undefined,
      }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onClick={handleMediaAreaClick}
    >
      <CardMedia
        component="img"
        className="vitrine-card-media"
        image={currentUrl}
        alt={`${vehicle.brand} ${vehicle.model} ${vehicle.year}`}
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          transition: 'transform 0.45s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          userSelect: 'none',
          WebkitUserDrag: 'none',
        }}
        draggable={false}
      />
      {canSwipe && (
        <Stack
          direction="row"
          spacing={0.5}
          justifyContent="center"
          sx={{
            position: 'absolute',
            bottom: 10,
            left: 0,
            right: 0,
            pointerEvents: 'none',
          }}
        >
          {imageUrls.map((_, i) => (
            <Box
              key={i}
              sx={{
                width: i === photoIndex ? 14 : 6,
                height: 6,
                borderRadius: 3,
                bgcolor: i === photoIndex ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.4)',
                boxShadow: '0 1px 3px rgba(0,0,0,0.35)',
                transition: 'width 0.2s ease, background-color 0.2s ease',
              }}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
};

const VitrinePage: React.FC = () => {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterBrand, setFilterBrand] = useState<string>('');
  const [filterYearMin, setFilterYearMin] = useState<string>('');
  const [filterYearMax, setFilterYearMax] = useState<string>('');
  const [filterPriceMin, setFilterPriceMin] = useState<string>('');
  const [filterPriceMax, setFilterPriceMax] = useState<string>('');
  const [filterKmMin, setFilterKmMin] = useState<string>('');
  const [filterKmMax, setFilterKmMax] = useState<string>('');
  const [sort, setSort] = useState<SortOption>('relevance');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  useEffect(() => {
    trackShowcaseEvent('page_view');
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const data = await publicVehicleService.getAvailableVehicles();
        setVehicles(data);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const formatCurrency = (value: number) =>
    value?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) ?? '';

  /** Preço de venda para filtro/ordem: usa sale_price ou fallback em purchase_price */
  const getSalePrice = (v: Vehicle) => {
    const sale = v.sale_price;
    if (sale != null && Number(sale) > 0) return Number(sale);
    return v.purchase_price || 0;
  };
  /** Preço promocional (se houver) */
  const getSalePricePromo = (v: Vehicle) => {
    const promo = v.sale_price_promo;
    if (promo != null && Number(promo) > 0) return Number(promo);
    return null;
  };

  const brands = useMemo(() => {
    const set = new Set(vehicles.map((v) => v.brand).filter(Boolean));
    return Array.from(set).sort();
  }, [vehicles]);

  const priceRange = useMemo(() => {
    if (!vehicles.length) return { min: 0, max: 500000 };
    const prices = vehicles.map((v) => getSalePrice(v)).filter((n) => n > 0);
    return {
      min: prices.length ? Math.min(...prices) : 0,
      max: prices.length ? Math.max(...prices) : 500000,
    };
  }, [vehicles]);

  const kmRange = useMemo(() => {
    if (!vehicles.length) return { min: 0, max: 200000 };
    const kms = vehicles.map((v) => parseKm(v.km || '')).filter((n) => n > 0);
    return {
      min: 0,
      max: kms.length ? Math.max(...kms) : 200000,
    };
  }, [vehicles]);

  const yearBounds = useMemo(() => {
    if (!vehicles.length) return { min: 1990, max: new Date().getFullYear() + 1 };
    const ys = vehicles.map((v) => v.year);
    return { min: Math.min(...ys), max: Math.max(...ys) };
  }, [vehicles]);

  const hasActiveFiltersOrSearch = useMemo(() => {
    return (
      !!search.trim() ||
      !!(filterBrand || filterYearMin || filterYearMax || filterPriceMin || filterPriceMax || filterKmMin || filterKmMax)
    );
  }, [search, filterBrand, filterYearMin, filterYearMax, filterPriceMin, filterPriceMax, filterKmMin, filterKmMax]);

  const sortByShowcase = useCallback((a: Vehicle, b: Vehicle) => {
    const ap = Number(a.showcase_priority ?? 0) || 0;
    const bp = Number(b.showcase_priority ?? 0) || 0;
    if (bp !== ap) return bp - ap;

    // Fallback: mais novo primeiro (id maior costuma ser mais recente)
    return (Number(b.id) || 0) - (Number(a.id) || 0);
  }, []);

  const topVehicles = useMemo(() => {
    if (hasActiveFiltersOrSearch) return [];
    return vehicles.filter((v) => !!v.showcase_top).sort(sortByShowcase).slice(0, 12);
  }, [vehicles, hasActiveFiltersOrSearch, sortByShowcase]);

  const featuredVehicles = useMemo(() => {
    if (hasActiveFiltersOrSearch) return [];
    return vehicles.filter((v) => !!v.showcase_featured).sort(sortByShowcase).slice(0, 12);
  }, [vehicles, hasActiveFiltersOrSearch, sortByShowcase]);

  const weekVehicles = useMemo(() => {
    if (hasActiveFiltersOrSearch) return [];
    return vehicles.filter((v) => !!v.showcase_week_highlight).sort(sortByShowcase).slice(0, 12);
  }, [vehicles, hasActiveFiltersOrSearch, sortByShowcase]);

  const filteredAndSorted = useMemo(() => {
    let list = vehicles.filter((v) => {
      if (search.trim()) {
        const q = search.toLowerCase();
        const match =
          [v.brand, v.model, v.plate, v.color, String(v.year), v.km].some((s) =>
            String(s).toLowerCase().includes(q)
          );
        if (!match) return false;
      }
      if (filterBrand && v.brand !== filterBrand) return false;
      if (filterYearMin && v.year < Number(filterYearMin)) return false;
      if (filterYearMax && v.year > Number(filterYearMax)) return false;
      const price = getSalePrice(v);
      if (filterPriceMin && price < Number(filterPriceMin)) return false;
      if (filterPriceMax && price > Number(filterPriceMax)) return false;
      const vKm = parseKm(v.km || '');
      if (filterKmMin && vKm < Number(filterKmMin)) return false;
      if (filterKmMax && vKm > Number(filterKmMax)) return false;
      return true;
    });
    if (sort === 'price_asc') list = [...list].sort((a, b) => getSalePrice(a) - getSalePrice(b));
    else if (sort === 'price_desc') list = [...list].sort((a, b) => getSalePrice(b) - getSalePrice(a));
    else if (sort === 'year_desc') list = [...list].sort((a, b) => b.year - a.year);
    else {
      // Relevância: quando não há filtros, prioriza vitrine (topo/destaque/semana) + prioridade
      if (!hasActiveFiltersOrSearch) {
        list = [...list].sort((a, b) => {
          const aScore =
            (a.showcase_top ? 300 : 0) + (a.showcase_featured ? 200 : 0) + (a.showcase_week_highlight ? 100 : 0);
          const bScore =
            (b.showcase_top ? 300 : 0) + (b.showcase_featured ? 200 : 0) + (b.showcase_week_highlight ? 100 : 0);
          if (bScore !== aScore) return bScore - aScore;
          return sortByShowcase(a, b);
        });
      }
    }
    return list;
  }, [vehicles, search, filterBrand, filterYearMin, filterYearMax, filterPriceMin, filterPriceMax, filterKmMin, filterKmMax, sort, hasActiveFiltersOrSearch, sortByShowcase]);

  const hasActiveFilters =
    filterBrand || filterYearMin || filterYearMax || filterPriceMin || filterPriceMax || filterKmMin || filterKmMax;

  const clearFilters = useCallback(() => {
    setFilterBrand('');
    setFilterYearMin('');
    setFilterYearMax('');
    setFilterPriceMin('');
    setFilterPriceMax('');
    setFilterKmMin('');
    setFilterKmMax('');
  }, []);

  const filterChips = useMemo(() => {
    const out: { key: string; label: string; onDelete: () => void }[] = [];
    if (filterBrand) {
      out.push({ key: 'brand', label: `Marca: ${filterBrand}`, onDelete: () => setFilterBrand('') });
    }
    if (filterYearMin) {
      out.push({ key: 'ymin', label: `Ano ≥ ${filterYearMin}`, onDelete: () => setFilterYearMin('') });
    }
    if (filterYearMax) {
      out.push({ key: 'ymax', label: `Ano ≤ ${filterYearMax}`, onDelete: () => setFilterYearMax('') });
    }
    if (filterPriceMin) {
      out.push({
        key: 'pmin',
        label: `Preço ≥ ${Number(filterPriceMin).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`,
        onDelete: () => setFilterPriceMin(''),
      });
    }
    if (filterPriceMax) {
      out.push({
        key: 'pmax',
        label: `Preço ≤ ${Number(filterPriceMax).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`,
        onDelete: () => setFilterPriceMax(''),
      });
    }
    if (filterKmMin) {
      out.push({
        key: 'kmin',
        label: `Km ≥ ${Number(filterKmMin).toLocaleString('pt-BR')}`,
        onDelete: () => setFilterKmMin(''),
      });
    }
    if (filterKmMax) {
      out.push({
        key: 'kmax',
        label: `Km ≤ ${Number(filterKmMax).toLocaleString('pt-BR')}`,
        onDelete: () => setFilterKmMax(''),
      });
    }
    return out;
  }, [
    filterBrand,
    filterYearMin,
    filterYearMax,
    filterPriceMin,
    filterPriceMax,
    filterKmMin,
    filterKmMax,
  ]);

  const priceSliderStep = Math.max(1000, Math.round((priceRange.max - priceRange.min) / 120) || 1000);
  const kmSliderStep = Math.max(500, Math.round((kmRange.max - kmRange.min) / 120) || 500);

  const sectionTitle = (icon: React.ReactNode, text: string) => (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
      <Box sx={{ color: BRAND_RED, display: 'flex', alignItems: 'center', opacity: 0.95 }}>{icon}</Box>
      <Typography
        variant="overline"
        sx={{
          fontFamily: fontHeading,
          fontWeight: 700,
          fontSize: 11,
          letterSpacing: '0.08em',
          color: 'rgba(18,18,18,0.55)',
          lineHeight: 1.2,
        }}
      >
        {text}
      </Typography>
    </Stack>
  );

  const renderFilterPanel = (inDrawer?: boolean) => (
    <Paper
      elevation={0}
      sx={{
        borderRadius: inDrawer ? 0 : 3,
        overflow: 'hidden',
        border: inDrawer ? 'none' : '1px solid rgba(0,0,0,0.06)',
        boxShadow: inDrawer ? 'none' : '0 8px 32px rgba(0,0,0,0.06)',
        bgcolor: '#fff',
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 2,
          background: 'linear-gradient(135deg, rgba(221,47,48,0.06) 0%, rgba(0,0,0,0.02) 100%)',
          borderBottom: '1px solid rgba(0,0,0,0.06)',
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <TuneIcon sx={{ color: BRAND_RED, fontSize: 22 }} />
            <Box>
              <Typography sx={{ fontFamily: fontHeading, fontWeight: 700, fontSize: 16, color: '#121212', lineHeight: 1.2 }}>
                Refinar busca
              </Typography>
              <Typography sx={{ fontFamily: fontBody, fontSize: 12, color: 'rgba(18,18,18,0.55)', mt: 0.25 }}>
                Ajuste os critérios para encontrar o veículo ideal
              </Typography>
            </Box>
          </Stack>
          {hasActiveFilters && (
            <Button
              size="small"
              onClick={clearFilters}
              sx={{
                color: BRAND_RED,
                fontFamily: fontBody,
                fontWeight: 600,
                textTransform: 'none',
                fontSize: 13,
                whiteSpace: 'nowrap',
              }}
            >
              Limpar tudo
            </Button>
          )}
        </Stack>
      </Box>

      <Stack divider={<Divider flexItem sx={{ borderColor: 'rgba(0,0,0,0.06)' }} />}>
        <Box sx={{ px: 2.5, py: 2.5 }}>
          {sectionTitle(<CategoryIcon sx={{ fontSize: 20 }} />, 'Marca')}
          <Autocomplete
            size="small"
            options={brands}
            value={filterBrand || null}
            onChange={(_, v) => setFilterBrand(v || '')}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Todas as marcas"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontFamily: fontBody,
                    bgcolor: 'rgba(0,0,0,0.02)',
                    borderRadius: 2,
                  },
                }}
              />
            )}
          />
        </Box>

        <Box sx={{ px: 2.5, py: 2.5 }}>
          {sectionTitle(<CalendarIcon sx={{ fontSize: 20 }} />, 'Ano do modelo')}
          <Stack direction="row" spacing={1.5}>
            <TextField
              size="small"
              fullWidth
              label="De"
              type="number"
              value={filterYearMin}
              onChange={(e) => setFilterYearMin(e.target.value)}
              inputProps={{ min: yearBounds.min, max: yearBounds.max }}
              sx={{
                '& .MuiOutlinedInput-root': { fontFamily: fontBody, borderRadius: 2, bgcolor: 'rgba(0,0,0,0.02)' },
              }}
            />
            <TextField
              size="small"
              fullWidth
              label="Até"
              type="number"
              value={filterYearMax}
              onChange={(e) => setFilterYearMax(e.target.value)}
              inputProps={{ min: yearBounds.min, max: yearBounds.max }}
              sx={{
                '& .MuiOutlinedInput-root': { fontFamily: fontBody, borderRadius: 2, bgcolor: 'rgba(0,0,0,0.02)' },
              }}
            />
          </Stack>
        </Box>

        <Box sx={{ px: 2.5, py: 2.5 }}>
          {sectionTitle(<PaymentsIcon sx={{ fontSize: 20 }} />, 'Faixa de preço')}
          <Typography sx={{ fontFamily: fontBody, fontSize: 12, color: 'rgba(18,18,18,0.5)', mb: 1 }}>
            Arraste ou informe valores em reais
          </Typography>
          <Slider
            value={[
              filterPriceMin ? Number(filterPriceMin) : priceRange.min,
              filterPriceMax ? Number(filterPriceMax) : priceRange.max,
            ]}
            min={priceRange.min}
            max={priceRange.max}
            step={priceSliderStep}
            valueLabelDisplay="auto"
            disableSwap
            valueLabelFormat={(v) =>
              v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })
            }
            onChange={(_, value) => {
              const [min, max] = value as number[];
              setFilterPriceMin(String(min));
              setFilterPriceMax(String(max));
            }}
            sx={{
              color: BRAND_RED,
              mt: 0.5,
              '& .MuiSlider-thumb': { width: 18, height: 18 },
              '& .MuiSlider-track': { border: 'none' },
            }}
          />
          <Stack direction="row" spacing={1.5} sx={{ mt: 1 }}>
            <TextField
              size="small"
              fullWidth
              label="Mín. (R$)"
              type="number"
              value={filterPriceMin}
              onChange={(e) => setFilterPriceMin(e.target.value)}
              inputProps={{ min: priceRange.min, max: priceRange.max }}
              sx={{
                '& .MuiOutlinedInput-root': { fontFamily: fontBody, borderRadius: 2, bgcolor: 'rgba(0,0,0,0.02)', fontSize: 13 },
              }}
            />
            <TextField
              size="small"
              fullWidth
              label="Máx. (R$)"
              type="number"
              value={filterPriceMax}
              onChange={(e) => setFilterPriceMax(e.target.value)}
              inputProps={{ min: priceRange.min, max: priceRange.max }}
              sx={{
                '& .MuiOutlinedInput-root': { fontFamily: fontBody, borderRadius: 2, bgcolor: 'rgba(0,0,0,0.02)', fontSize: 13 },
              }}
            />
          </Stack>
        </Box>

        <Box sx={{ px: 2.5, py: 2.5 }}>
          {sectionTitle(<SpeedIcon sx={{ fontSize: 20 }} />, 'Quilometragem')}
          <Typography sx={{ fontFamily: fontBody, fontSize: 12, color: 'rgba(18,18,18,0.5)', mb: 1 }}>
            Hodômetro aproximado (km)
          </Typography>
          <Slider
            value={[
              filterKmMin ? Number(filterKmMin) : kmRange.min,
              filterKmMax ? Number(filterKmMax) : kmRange.max,
            ]}
            min={kmRange.min}
            max={kmRange.max}
            step={kmSliderStep}
            valueLabelDisplay="auto"
            disableSwap
            valueLabelFormat={(v) =>
              v >= 1000 ? `${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)} mil km` : `${v} km`
            }
            onChange={(_, value) => {
              const [min, max] = value as number[];
              setFilterKmMin(String(min));
              setFilterKmMax(String(max));
            }}
            sx={{
              color: BRAND_RED,
              mt: 0.5,
              '& .MuiSlider-thumb': { width: 18, height: 18 },
              '& .MuiSlider-track': { border: 'none' },
            }}
          />
          <Stack direction="row" spacing={1.5} sx={{ mt: 1 }}>
            <TextField
              size="small"
              fullWidth
              label="Mín. km"
              type="number"
              value={filterKmMin}
              onChange={(e) => setFilterKmMin(e.target.value)}
              inputProps={{ min: kmRange.min, max: kmRange.max }}
              sx={{
                '& .MuiOutlinedInput-root': { fontFamily: fontBody, borderRadius: 2, bgcolor: 'rgba(0,0,0,0.02)', fontSize: 13 },
              }}
            />
            <TextField
              size="small"
              fullWidth
              label="Máx. km"
              type="number"
              value={filterKmMax}
              onChange={(e) => setFilterKmMax(e.target.value)}
              inputProps={{ min: kmRange.min, max: kmRange.max }}
              sx={{
                '& .MuiOutlinedInput-root': { fontFamily: fontBody, borderRadius: 2, bgcolor: 'rgba(0,0,0,0.02)', fontSize: 13 },
              }}
            />
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );

  return (
    <Box sx={{ bgcolor: '#F5F5F5', minHeight: '100vh', color: '#121212' }}>
      {/* ========== NAVBAR estilo Webmotors: branco, limpo ========== */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: '#fff',
          color: '#121212',
          borderBottom: '1px solid rgba(0,0,0,0.08)',
        }}
      >
        <Toolbar sx={{ maxWidth: 1280, mx: 'auto', width: '100%', justifyContent: 'space-between', py: 1.5, px: 2 }}>
          <Link to="/vitrine" style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center' }}>
            <img
              src={`${process.env.PUBLIC_URL || ''}/assets/logos/MARCA - R MOTORS horizontal.png`}
              alt="R Motors"
              style={{ height: 36, width: 'auto' }}
            />
          </Link>
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 3 }}>
            <Typography component={Link} to="/vitrine" sx={{ fontFamily: fontBody, fontWeight: 500, color: '#121212', textDecoration: 'none', fontSize: 15 }}>
              Comprar
            </Typography>
            <Typography component={Link} to="/vitrine#veiculos" sx={{ fontFamily: fontBody, fontWeight: 500, color: '#121212', textDecoration: 'none', fontSize: 15 }}>
              Veículos
            </Typography>
            <Button
              component="a"
              href={storeWhatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackShowcaseEvent('whatsapp_click')}
              variant="contained"
              size="medium"
              sx={{
                fontFamily: fontHeading,
                fontWeight: 600,
                fontSize: 14,
                bgcolor: '#DD2F30',
                color: '#fff',
                px: 2,
                py: 1,
                '&:hover': { bgcolor: '#c22829' },
              }}
            >
              Fale conosco
            </Button>
          </Box>
          <IconButton
            sx={{ display: { md: 'none' } }}
            component="a"
            href={storeWhatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackShowcaseEvent('whatsapp_click')}
            size="small"
          >
            <Typography sx={{ fontFamily: fontBody, fontSize: 14, color: '#DD2F30' }}>Fale conosco</Typography>
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* ========== BANNER / HERO com imagem de fundo ========== */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          // A imagem do banner é bem panorâmica; manter proporção evita “corte” e área branca no mobile.
          // Proporção aproximada do arquivo (largura/altura).
          aspectRatio: { xs: '1024 / 240', md: 'auto' },
          minHeight: { md: 360 },
          px: { xs: 0, md: 2 },
          borderBottom: '1px solid rgba(0,0,0,0.06)',
          backgroundImage: `url(${process.env.PUBLIC_URL || ''}/assets/vitrine/Note%20-%20Branco.png)`,
          backgroundColor: '#fff',
          backgroundSize: { xs: 'contain', md: 'cover' },
          backgroundPosition: { xs: 'top center', md: 'center' },
          backgroundRepeat: 'no-repeat',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, height: '100%' }}>
          <Typography
            component="h1"
            sx={{
              fontFamily: fontHeading,
              fontWeight: 600,
              fontSize: { xs: 24, sm: 28, md: 32 },
              color: '#fff',
              textAlign: 'center',
              textShadow: '0 1px 3px rgba(0,0,0,0.5)',
            }}
          >
          </Typography>
        </Container>
      </Box>

      {/* ========== BUSCA + chips de filtros ativos ========== */}
      <Box sx={{ bgcolor: '#fff', py: { xs: 2, md: 2.5 }, px: 2, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
        <Container maxWidth="lg">
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: '1px solid rgba(0,0,0,0.08)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
              overflow: 'hidden',
            }}
          >
            <TextField
              fullWidth
              placeholder="Busque por marca, modelo, placa, cor ou ano..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'rgba(18,18,18,0.35)' }} />
                  </InputAdornment>
                ),
                sx: {
                  fontFamily: fontBody,
                  fontSize: 16,
                  py: 0.25,
                  bgcolor: 'rgba(0,0,0,0.02)',
                  borderRadius: 2,
                  '& fieldset': { borderColor: 'rgba(0,0,0,0.08)' },
                  '&:hover fieldset': { borderColor: 'rgba(0,0,0,0.15)' },
                },
              }}
              inputProps={{ 'aria-label': 'Buscar veículos' }}
            />
          </Paper>
          {filterChips.length > 0 && (
            <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1} sx={{ mt: 2 }}>
              <Typography sx={{ fontFamily: fontBody, fontSize: 13, color: 'rgba(18,18,18,0.5)', mr: 0.5 }}>
                Filtros ativos:
              </Typography>
              {filterChips.map((c) => (
                <Chip
                  key={c.key}
                  label={c.label}
                  size="small"
                  onDelete={c.onDelete}
                  sx={{
                    fontFamily: fontBody,
                    fontWeight: 500,
                    bgcolor: 'rgba(221,47,48,0.08)',
                    border: '1px solid rgba(221,47,48,0.2)',
                    '& .MuiChip-deleteIcon': { color: BRAND_RED },
                  }}
                />
              ))}
              <Button
                size="small"
                onClick={clearFilters}
                sx={{ fontFamily: fontBody, textTransform: 'none', color: BRAND_RED, fontWeight: 600, fontSize: 13 }}
              >
                Limpar todos
              </Button>
            </Stack>
          )}
        </Container>
      </Box>

      {/* ========== CONTEÚDO: sidebar filtros + listagem ========== */}
      <Container maxWidth="lg" sx={{ py: 3, px: 2 }}>
        <Grid container spacing={3}>
          {/* Sidebar filtros (desktop) */}
          <Grid item xs={12} md={3} sx={{ display: { xs: 'none', md: 'block' } }}>
            <Box sx={{ position: 'sticky', top: 24 }}>{renderFilterPanel()}</Box>
          </Grid>

          {/* Área principal: ordenação + grid */}
          <Grid item xs={12} md={9}>
            {/* Botão abrir filtros no mobile */}
            <Box sx={{ display: { md: 'none' }, mb: 2 }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<FilterIcon />}
                onClick={() => setMobileFiltersOpen(true)}
                sx={{
                  fontFamily: fontHeading,
                  fontWeight: 600,
                  py: 1.25,
                  borderRadius: 2,
                  borderColor: 'rgba(0,0,0,0.12)',
                  color: '#121212',
                  textTransform: 'none',
                  justifyContent: 'center',
                  '&:hover': { borderColor: BRAND_RED, bgcolor: 'rgba(221,47,48,0.04)' },
                }}
              >
                Refinar busca
                {hasActiveFilters && (
                  <Chip
                    label={filterChips.length}
                    size="small"
                    sx={{ ml: 1, bgcolor: BRAND_RED, color: '#fff', fontWeight: 700, minWidth: 22 }}
                  />
                )}
              </Button>
            </Box>
            <Drawer
              anchor="left"
              open={mobileFiltersOpen}
              onClose={() => setMobileFiltersOpen(false)}
              PaperProps={{
                sx: {
                  width: { xs: 'min(100%, 360px)', sm: 360 },
                  borderRight: '1px solid rgba(0,0,0,0.08)',
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2,
                  py: 1.5,
                  borderBottom: '1px solid rgba(0,0,0,0.06)',
                  bgcolor: 'rgba(0,0,0,0.02)',
                }}
              >
                <Typography sx={{ fontFamily: fontHeading, fontWeight: 700, fontSize: 17 }}>
                  Filtros
                </Typography>
                <IconButton onClick={() => setMobileFiltersOpen(false)} aria-label="Fechar filtros" size="small">
                  <CloseIcon />
                </IconButton>
              </Box>
              <Box sx={{ overflow: 'auto', maxHeight: 'calc(100vh - 56px)' }}>{renderFilterPanel(true)}</Box>
            </Drawer>

            {/* Ordenar + resultado */}
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                mb: 2,
              }}
            >
              <Typography sx={{ fontFamily: fontBody, fontSize: 14, color: '#505050' }}>
                {filteredAndSorted.length} {filteredAndSorted.length === 1 ? 'veículo' : 'veículos'} encontrado{filteredAndSorted.length !== 1 ? 's' : ''}
              </Typography>
              <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 220 } }}>
                <InputLabel id="sort-label">Ordenar por</InputLabel>
                <Select
                  labelId="sort-label"
                  label="Ordenar por"
                  value={sort}
                  onChange={(e: SelectChangeEvent<SortOption>) => setSort(e.target.value as SortOption)}
                  sx={{
                    fontFamily: fontBody,
                    bgcolor: '#fff',
                    borderRadius: 2,
                    '& fieldset': { borderColor: 'rgba(0,0,0,0.1)' },
                  }}
                >
                  <MenuItem value="relevance">Mais relevantes</MenuItem>
                  <MenuItem value="price_asc">Menor preço</MenuItem>
                  <MenuItem value="price_desc">Maior preço</MenuItem>
                  <MenuItem value="year_desc">Ano mais novo</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Seções controladas pelo cadastro (somente sem busca/filtros) */}
            {!loading && !hasActiveFiltersOrSearch && (topVehicles.length > 0 || featuredVehicles.length > 0 || weekVehicles.length > 0) && (
              <Box sx={{ mb: 3 }}>
                {topVehicles.length > 0 && (
                  <Box sx={{ mb: 2.5 }}>
                    <Typography sx={{ fontFamily: fontHeading, fontWeight: 800, fontSize: { xs: 18, sm: 20 }, mb: 1.25 }}>
                      Topo da vitrine
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 1 }}>
                      {topVehicles.map((v) => (
                        <Card
                          key={`top-${v.id}`}
                          component={Link}
                          to={`/vitrine/veiculo/${v.id}`}
                          elevation={0}
                          sx={{
                            minWidth: { xs: 260, sm: 320 },
                            maxWidth: { xs: 260, sm: 320 },
                            borderRadius: 3,
                            overflow: 'hidden',
                            border: '1px solid rgba(0,0,0,0.06)',
                            bgcolor: '#fff',
                            textDecoration: 'none',
                            color: 'inherit',
                            boxShadow: '0 8px 28px rgba(0,0,0,0.08)',
                            flex: '0 0 auto',
                          }}
                        >
                          <CardMedia
                            image={v.images?.[0]?.url ? getImageUrl(v.images[0].url) : undefined}
                            sx={{ height: 170, bgcolor: 'rgba(0,0,0,0.06)' }}
                          />
                          <CardContent>
                            <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
                              <Chip size="small" label="Topo" sx={{ bgcolor: 'rgba(221,47,48,0.12)', color: BRAND_RED, fontFamily: fontBody }} />
                              {v.showcase_featured && <Chip size="small" label="Destaque" variant="outlined" sx={{ fontFamily: fontBody }} />}
                              {v.showcase_week_highlight && <Chip size="small" label="Semana" variant="outlined" sx={{ fontFamily: fontBody }} />}
                            </Stack>
                            <Typography sx={{ fontFamily: fontHeading, fontWeight: 800, fontSize: 16, color: '#121212' }}>
                              {v.brand} {v.model}
                            </Typography>
                            <Typography sx={{ fontFamily: fontBody, fontSize: 13, color: 'rgba(18,18,18,0.6)' }}>
                              {v.year} • {v.km ? `${v.km} km` : '—'}
                            </Typography>
                          </CardContent>
                        </Card>
                      ))}
                    </Box>
                  </Box>
                )}

                {featuredVehicles.length > 0 && (
                  <Box sx={{ mb: 2.5 }}>
                    <Typography sx={{ fontFamily: fontHeading, fontWeight: 800, fontSize: { xs: 18, sm: 20 }, mb: 1.25 }}>
                      Carros em destaque
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 1 }}>
                      {featuredVehicles.map((v) => (
                        <Paper
                          key={`feat-${v.id}`}
                          component={Link}
                          to={`/vitrine/veiculo/${v.id}`}
                          elevation={0}
                          sx={{
                            minWidth: { xs: 220, sm: 260 },
                            maxWidth: { xs: 220, sm: 260 },
                            p: 1.25,
                            borderRadius: 3,
                            border: '1px solid rgba(0,0,0,0.06)',
                            bgcolor: '#fff',
                            textDecoration: 'none',
                            color: 'inherit',
                            flex: '0 0 auto',
                          }}
                        >
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Avatar
                              variant="rounded"
                              src={v.images?.[0]?.url ? getImageUrl(v.images[0].url) : undefined}
                              sx={{ width: 58, height: 58, bgcolor: 'rgba(0,0,0,0.06)' }}
                            />
                            <Box sx={{ minWidth: 0 }}>
                              <Typography sx={{ fontFamily: fontHeading, fontWeight: 800, fontSize: 14, color: '#121212' }} noWrap>
                                {v.brand} {v.model}
                              </Typography>
                              <Typography sx={{ fontFamily: fontBody, fontSize: 12, color: 'rgba(18,18,18,0.6)' }} noWrap>
                                {v.year} • {v.km ? `${v.km} km` : '—'}
                              </Typography>
                              <Chip size="small" label="Destaque" sx={{ mt: 0.75, fontFamily: fontBody }} />
                            </Box>
                          </Stack>
                        </Paper>
                      ))}
                    </Box>
                  </Box>
                )}

                {weekVehicles.length > 0 && (
                  <Box sx={{ mb: 1 }}>
                    <Typography sx={{ fontFamily: fontHeading, fontWeight: 800, fontSize: { xs: 18, sm: 20 }, mb: 1.25 }}>
                      Destaques da semana
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 1 }}>
                      {weekVehicles.map((v) => (
                        <Paper
                          key={`week-${v.id}`}
                          component={Link}
                          to={`/vitrine/veiculo/${v.id}`}
                          elevation={0}
                          sx={{
                            minWidth: { xs: 220, sm: 260 },
                            maxWidth: { xs: 220, sm: 260 },
                            p: 1.25,
                            borderRadius: 3,
                            border: '1px solid rgba(0,0,0,0.06)',
                            bgcolor: '#fff',
                            textDecoration: 'none',
                            color: 'inherit',
                            flex: '0 0 auto',
                          }}
                        >
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Avatar
                              variant="rounded"
                              src={v.images?.[0]?.url ? getImageUrl(v.images[0].url) : undefined}
                              sx={{ width: 58, height: 58, bgcolor: 'rgba(0,0,0,0.06)' }}
                            />
                            <Box sx={{ minWidth: 0 }}>
                              <Typography sx={{ fontFamily: fontHeading, fontWeight: 800, fontSize: 14, color: '#121212' }} noWrap>
                                {v.brand} {v.model}
                              </Typography>
                              <Typography sx={{ fontFamily: fontBody, fontSize: 12, color: 'rgba(18,18,18,0.6)' }} noWrap>
                                {v.year} • {v.km ? `${v.km} km` : '—'}
                              </Typography>
                              <Chip size="small" label="Semana" sx={{ mt: 0.75, fontFamily: fontBody }} />
                            </Box>
                          </Stack>
                        </Paper>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            )}

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                <CircularProgress sx={{ color: BRAND_RED }} size={44} thickness={4} />
              </Box>
            ) : filteredAndSorted.length === 0 ? (
              <Paper
                elevation={0}
                sx={{
                  textAlign: 'center',
                  py: 8,
                  px: 2,
                  borderRadius: 3,
                  border: '1px solid rgba(0,0,0,0.06)',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.06)',
                  bgcolor: '#fff',
                }}
              >
                <CarIcon sx={{ fontSize: 56, color: 'rgba(0,0,0,0.15)', mb: 2 }} />
                <Typography sx={{ fontFamily: fontHeading, fontWeight: 700, fontSize: 18, color: '#121212', mb: 1 }}>
                  Nenhum veículo encontrado
                </Typography>
                <Typography sx={{ fontFamily: fontBody, fontSize: 14, color: 'rgba(18,18,18,0.55)', mb: 2, maxWidth: 360, mx: 'auto' }}>
                  Tente ajustar os filtros ou limpar a busca para ver mais opções.
                </Typography>
                <Button
                  onClick={clearFilters}
                  variant="contained"
                  disableElevation
                  sx={{
                    mt: 1,
                    bgcolor: BRAND_RED,
                    fontFamily: fontHeading,
                    fontWeight: 700,
                    textTransform: 'none',
                    px: 3,
                    py: 1.25,
                    borderRadius: 2,
                    '&:hover': { bgcolor: '#c22829' },
                  }}
                >
                  Limpar filtros
                </Button>
              </Paper>
            ) : (
              <Grid container spacing={2.5} id="veiculos">
                {filteredAndSorted.map((v) => {
                  const hasPromo = getSalePricePromo(v) != null && getSalePricePromo(v)! < getSalePrice(v);
                  const idLabel = v.no_plate ? 'Sem placa' : v.plate_end ? `Placa final ${v.plate_end}` : null;
                  return (
                    <Grid item xs={12} sm={6} lg={4} key={v.id}>
                      <Card
                        component={Link}
                        to={`/vitrine/veiculo/${v.id}`}
                        elevation={0}
                        aria-labelledby={`vitrine-card-title-${v.id}`}
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          borderRadius: 3,
                          overflow: 'hidden',
                          border: '1px solid rgba(0,0,0,0.06)',
                          bgcolor: '#fff',
                          boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
                          textDecoration: 'none',
                          color: 'inherit',
                          transition: 'transform 0.28s ease, box-shadow 0.28s ease, border-color 0.28s ease',
                          cursor: 'pointer',
                          '&:focus-visible': {
                            outline: `2px solid ${BRAND_RED}`,
                            outlineOffset: 3,
                          },
                          '&:hover': {
                            boxShadow: '0 16px 48px rgba(0,0,0,0.12)',
                            transform: 'translateY(-6px)',
                            borderColor: 'rgba(221,47,48,0.22)',
                            '& .vitrine-card-media': {
                              transform: 'scale(1.05)',
                            },
                            '& .vitrine-card-cta': {
                              borderColor: BRAND_RED,
                              color: BRAND_RED,
                              bgcolor: 'rgba(221,47,48,0.04)',
                              '& .vitrine-card-cta-icon': {
                                transform: 'translateX(3px)',
                              },
                            },
                          },
                        }}
                      >
                        <Box sx={{ position: 'relative', overflow: 'hidden' }}>
                          <VitrineListingCardMedia vehicle={v} />
                          <Stack
                            direction="row"
                            spacing={1}
                            sx={{
                              position: 'absolute',
                              top: 12,
                              left: 12,
                              flexWrap: 'wrap',
                              gap: 0.75,
                            }}
                          >
                            <Chip
                              icon={<CalendarIcon sx={{ fontSize: '16px !important', color: '#fff !important' }} />}
                              label={v.year}
                              size="small"
                              sx={{
                                fontFamily: fontHeading,
                                fontWeight: 700,
                                fontSize: 12,
                                bgcolor: 'rgba(0,0,0,0.55)',
                                color: '#fff',
                                backdropFilter: 'blur(6px)',
                                '& .MuiChip-label': { px: 1 },
                              }}
                            />
                          </Stack>
                          {hasPromo && (
                            <Chip
                              icon={<LocalOfferIcon sx={{ fontSize: '16px !important', color: '#fff !important' }} />}
                              label="Oferta"
                              size="small"
                              sx={{
                                position: 'absolute',
                                top: 12,
                                right: 12,
                                fontFamily: fontHeading,
                                fontWeight: 700,
                                fontSize: 12,
                                bgcolor: BRAND_RED,
                                color: '#fff',
                                boxShadow: '0 4px 12px rgba(221,47,48,0.45)',
                              }}
                            />
                          )}
                        </Box>

                        <CardContent
                          sx={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            p: 2.5,
                            pt: 2.25,
                            gap: 1.25,
                          }}
                        >
                          <Typography
                            id={`vitrine-card-title-${v.id}`}
                            component="h2"
                            sx={{
                              fontFamily: fontHeading,
                              fontWeight: 700,
                              fontSize: { xs: 17, sm: 18 },
                              lineHeight: 1.35,
                              color: '#121212',
                              minHeight: { xs: '2.7em', sm: 'auto' },
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {v.brand} {v.model}
                          </Typography>

                          <Stack direction="row" flexWrap="wrap" useFlexGap gap={0.75}>
                            {idLabel && (
                              <Chip
                                label={idLabel}
                                size="small"
                                variant="outlined"
                                sx={{
                                  fontFamily: fontBody,
                                  fontSize: 12,
                                  height: 26,
                                  borderColor: 'rgba(0,0,0,0.12)',
                                  color: 'rgba(18,18,18,0.75)',
                                }}
                              />
                            )}
                            {v.km ? (
                              <Chip
                                icon={<SpeedIcon sx={{ fontSize: '14px !important' }} />}
                                label={`${v.km} km`}
                                size="small"
                                variant="outlined"
                                sx={{
                                  fontFamily: fontBody,
                                  fontSize: 12,
                                  height: 26,
                                  borderColor: 'rgba(0,0,0,0.12)',
                                  color: 'rgba(18,18,18,0.75)',
                                }}
                              />
                            ) : null}
                          </Stack>

                          <Divider sx={{ borderColor: 'rgba(0,0,0,0.06)', my: 0.25 }} />

                          <Box sx={{ mt: 'auto' }}>
                            <Typography
                              variant="overline"
                              sx={{
                                fontFamily: fontBody,
                                fontSize: 10,
                                letterSpacing: '0.12em',
                                color: 'rgba(18,18,18,0.45)',
                                display: 'block',
                                mb: 0.25,
                              }}
                            >
                              {hasPromo ? 'Preço promocional' : 'Valor'}
                            </Typography>
                            {hasPromo ? (
                              <Box>
                                <Typography
                                  sx={{
                                    fontFamily: fontBody,
                                    fontSize: 13,
                                    color: 'rgba(18,18,18,0.45)',
                                    textDecoration: 'line-through',
                                    mb: 0.25,
                                  }}
                                >
                                  {formatCurrency(getSalePrice(v))}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontFamily: fontHeading,
                                    fontWeight: 800,
                                    fontSize: 22,
                                    color: BRAND_RED,
                                    letterSpacing: '-0.02em',
                                    lineHeight: 1.2,
                                  }}
                                >
                                  {formatCurrency(getSalePricePromo(v)!)}
                                </Typography>
                              </Box>
                            ) : (
                              <Typography
                                sx={{
                                  fontFamily: fontHeading,
                                  fontWeight: 800,
                                  fontSize: 22,
                                  color: BRAND_RED,
                                  letterSpacing: '-0.02em',
                                  lineHeight: 1.2,
                                }}
                              >
                                {formatCurrency(getSalePrice(v))}
                              </Typography>
                            )}
                          </Box>

                          <Box
                            className="vitrine-card-cta"
                            component="span"
                            aria-hidden
                            sx={{
                              mt: 0.5,
                              py: 1.1,
                              px: 2,
                              borderRadius: 2,
                              width: '100%',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 1,
                              border: '1px solid rgba(0,0,0,0.14)',
                              color: 'rgba(18,18,18,0.78)',
                              bgcolor: 'transparent',
                              fontFamily: fontBody,
                              fontWeight: 600,
                              fontSize: 14,
                              transition: 'border-color 0.2s ease, color 0.2s ease, background-color 0.2s ease',
                            }}
                          >
                            Ver detalhes
                            <ArrowForwardIcon
                              className="vitrine-card-cta-icon"
                              sx={{
                                fontSize: 18,
                                opacity: 0.85,
                                transition: 'transform 0.2s ease',
                              }}
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Grid>
        </Grid>
      </Container>

      {/* ========== FOOTER estilo Webmotors: colunas ========== */}
      <Box
        component="footer"
        sx={{
          bgcolor: '#1a1a1a',
          color: 'rgba(255,255,255,0.85)',
          mt: 6,
          py: 6,
          px: 2,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} sm={6} md={3}>
              <img
                src={`${process.env.PUBLIC_URL || ''}/assets/logos/MARCA - R MOTORS horizontal.png`}
                alt="R Motors"
                style={{ height: 32, width: 'auto', filter: 'brightness(0) invert(1)' }}
              />
              <Typography sx={{ mt: 2, fontFamily: fontBody, color: 'rgba(255,255,255,0.6)', fontSize: 14 }}>
                Soluções em veículos com qualidade e transparência.
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography sx={{ fontFamily: fontHeading, fontWeight: 600, fontSize: 14, color: '#fff', mb: 1.5 }}>
                Comprar
              </Typography>
              <Typography component={Link} to="/vitrine" sx={{ display: 'block', fontFamily: fontBody, color: 'rgba(255,255,255,0.7)', textDecoration: 'none', fontSize: 14, mb: 0.5 }}>
                Veículos
              </Typography>
              <Typography component={Link} to="/vitrine#veiculos" sx={{ display: 'block', fontFamily: fontBody, color: 'rgba(255,255,255,0.7)', textDecoration: 'none', fontSize: 14 }}>
                Estoque
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography sx={{ fontFamily: fontHeading, fontWeight: 600, fontSize: 14, color: '#fff', mb: 1.5 }}>
                Ajuda
              </Typography>
              <Typography component="a" href={storeWhatsappLink} target="_blank" rel="noopener noreferrer" onClick={() => trackShowcaseEvent('whatsapp_click')} sx={{ display: 'block', fontFamily: fontBody, color: 'rgba(255,255,255,0.7)', textDecoration: 'none', fontSize: 14, mb: 0.5 }}>
                WhatsApp {STORE_BRANDING.whatsappDisplay}
              </Typography>
              <Typography sx={{ fontFamily: fontBody, color: 'rgba(255,255,255,0.7)', fontSize: 14 }}>
                Contato
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography sx={{ fontFamily: fontHeading, fontWeight: 600, fontSize: 14, color: '#fff', mb: 1.5 }}>
                Institucional
              </Typography>
              <Typography sx={{ fontFamily: fontBody, color: 'rgba(255,255,255,0.7)', fontSize: 14 }}>
                R Motors © {new Date().getFullYear()}
              </Typography>
            </Grid>
          </Grid>
          <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.1)', mt: 4, pt: 3 }}>
            <Typography sx={{ fontFamily: fontBody, fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>
              Todos os direitos reservados.
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default VitrinePage;
